import { useEffect, useState } from 'react';
import Popover from '../components/popover/Popover';
import api from 'services/api';
import { useSelector, useDispatch } from 'react-redux';
import { setSnackbar } from 'redux/snackbar';
import { ERROR_MESSAGES, filterMessageError } from './helpers';
import { formatDateTime } from 'utils';
import MeasurementsService from 'services/MeasurementsService';

export function useMeasurements() {
  const selectedCycle = useSelector(
    (state) => state.selectedCycle.selectedCycle,
  );
  const [measurements, setMeasurements] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [openProgress, setOpenProgress] = useState(false);
  const [errors, setErrors] = useState([]);
  const [errorsWhenPosting, setErrorsWhenPosting] = useState([]);

  const dispatch = useDispatch();

  const columns = [
    {
      title: 'Viveiro',
      field: 'cycle.pond.description',
      hideFilterIcon: true,
      editable: 'never',
    },
    {
      title: 'Data/Hora da medição',
      field: 'get_at',
      type: 'datetime',
      hideFilterIcon: true,
      render: (rowData) => <p>{formatDateTime(rowData.get_at)}</p>,
    },
    {
      title: 'Parâmetros',
      field: 'rules',
      editable: 'never',
      render: (rowData) => {
        let parametersvalue = '';
        rowData.parameters.forEach((parameter) => {
          parametersvalue
            += `${parameter.parameter.name
            }: ${
              parseFloat(parameter.value).toFixed(2)
            } ${
              parameter.parameter.unity.sigla
            }, `;
        });
        return <Popover data={parametersvalue.slice(0, -2)} />;
      },
      hideFilterIcon: true,
    },
  ];

  const columnsForExport = [
    {
      title: 'Ciclo',
      field: 'cycle',
    },
    {
      title: 'Viveiro',
      field: 'pond',
    },
    {
      title: 'Data',
      field: 'put_at',
    },
    {
      title: 'Hora',
      field: 'put_hour_at',
    },
    {
      title: 'Parâmetros',
      field: 'parameter',
    },
    {
      title: 'Valor',
      field: 'value',
    },
  ];

  useEffect(() => {
    async function loadData() {
      try {
        setOpenProgress(false);
        const response = await api.get(
          `/measurements/cycle-code/${selectedCycle.id}`,
        );

        if (response.data) {
          const data = await response.data;
          setMeasurements(data);
          setOpenProgress(true);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
      } finally {
        setOpenProgress(true);
      }
    }
    loadData();
  }, [selectedCycle]);

  useEffect(() => {
    async function loadMeasurementsExport() {
      try {
        const response = await (await api.get(`/measurements/cycle-code/${selectedCycle.id}/export`)).data;

        if (response.length === 0) {
          setOpenProgress(true);
        }
        if (response) {
          setExportData(response);
        }
      } catch (err) {
        setErrors((prev) => [err.message, ...prev]);
        setOpenProgress(true);
      }
    }
    loadMeasurementsExport();
  }, [selectedCycle]);

  const handleCloseSnackbar = () => {
    setErrorsWhenPosting([]);
  };

  // eslint-disable-next-line consistent-return
  const handleImportDataPost = async (payload) => {
    if (!payload) throw new Error('Payload is required.');
    try {
      setOpenProgress(false);
      const result = await MeasurementsService.importData(payload);
      const {
        errorsData, importedData, status, successMessage,
      } = result;

      if (status === 201) {
        if (importedData.length === 0) {
          if (errorsData.length > 0) {
            return setErrorsWhenPosting(filterMessageError(errorsData));
          }
          return dispatch(
            setSnackbar(
              true,
              'error',
              ERROR_MESSAGES.EMPTY_DATA,
            ),
          );
        }
        if (importedData.length > 0) {
          const updatedList = await MeasurementsService.measurementsList(selectedCycle.id);
          setMeasurements(updatedList);
          dispatch(
            setSnackbar(
              true,
              'success',
              successMessage,
            ),
          );
          if (errorsData.length > 0) {
            setErrorsWhenPosting(filterMessageError(errorsData));
          }
          setOpenProgress(true);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || ERROR_MESSAGES.INSERT_NEW_DATA;
      dispatch(
        setSnackbar(
          true,
          'error',
          errorMessage,
        ),
      );
      throw new Error(errorMessage);
    }
  };

  return {
    measurements,
    columns,
    columnsForExport,
    exportData,
    openProgress,
    errors,
    errorsWhenPosting,
    handleCloseSnackbar,
    handleImportDataPost,
  };
}
